import { useState } from "react";
import { format } from "date-fns";
import { ArrowRight, CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[#71717A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</label>
);
const Input = ({ testid, ...props }) => <input data-testid={testid} className="brutalist-input" {...props} />;
const TextArea = ({ testid, ...props }) => <textarea data-testid={testid} className="brutalist-input min-h-[80px] resize-y" {...props} />;

export default function DomesticEICStep1({ formData, updateForm, onNext }) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const set = (field) => (e) => updateForm({ [field]: e.target.value });

  const handleNext = () => {
    if (!formData.client_name.trim()) { toast.error("Please enter client name and address"); return; }
    if (!formData.installation_address.trim()) { toast.error("Please enter the installation address"); return; }
    if (!formData.designer_name.trim()) { toast.error("Please enter the designer/installer name"); return; }
    onNext();
  };

  return (
    <div className="form-container max-w-3xl mx-auto" data-testid="eic-step-1">
      <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Electrical Installation Certificate
      </h2>
      <p className="text-xs text-[#71717A] mb-6">Requirements for Electrical Installations — BS 7671 IET Wiring Regulations</p>

      {/* Client Details */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Details of the Client</h3>
        <Label>Client name and address</Label>
        <TextArea testid="eic-client-name" value={formData.client_name} onChange={set("client_name")} placeholder="Client name and full address" rows={2} />
        <div className="mt-2">
          <Label>Client email (for certificate delivery)</Label>
          <Input testid="eic-client-email" type="email" value={formData.client_email} onChange={set("client_email")} placeholder="client@email.com (optional)" />
        </div>
      </div>

      {/* Installation Details */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Details of the Installation</h3>
        <div className="space-y-3">
          <div className="form-field">
            <Label>Description of the installation</Label>
            <Input testid="eic-install-desc" value={formData.installation_description} onChange={set("installation_description")} placeholder="e.g. Domestic electrical installation" />
          </div>
          <div className="form-field">
            <Label>Extent of installation work covered by this certificate</Label>
            <Input testid="eic-install-extent" value={formData.installation_extent} onChange={set("installation_extent")} placeholder="e.g. Full rewire, new consumer unit" />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Address of the Installation</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-3">
            <Label>Installation address</Label>
            <Input testid="eic-install-address" value={formData.installation_address} onChange={set("installation_address")} placeholder="Full address" />
          </div>
          <div className="form-field">
            <Label>Postcode</Label>
            <Input testid="eic-install-postcode" value={formData.installation_postcode} onChange={set("installation_postcode")} placeholder="e.g. M1 1AE" />
          </div>
        </div>
        <div className="mt-3">
          <Label>The installation is</Label>
          <div className="flex gap-4">
            {["New", "An addition", "An alteration"].map((type) => (
              <label key={type} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="installation_type" value={type} checked={formData.installation_type === type}
                  onChange={(e) => updateForm({ installation_type: e.target.value })}
                  className="w-4 h-4 accent-[#00A3E0]" />
                <span className="text-sm">{type}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Departures & Signatures */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Design, Construction, Inspection & Testing</h3>
        <div className="space-y-3">
          <div className="form-field">
            <Label>Departures from BS 7671 (if any)</Label>
            <Input testid="eic-departures" value={formData.departures} onChange={set("departures")} placeholder="N/A" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="form-field">
              <Label>Designer / Inspector name</Label>
              <Input testid="eic-designer-name" value={formData.designer_name} onChange={set("designer_name")} />
            </div>
            <div className="form-field">
              <Label>Constructor name (if different)</Label>
              <Input testid="eic-constructor-name" value={formData.constructor_name} onChange={set("constructor_name")} />
            </div>
          </div>
          <div className="form-field">
            <Label>Date</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button data-testid="eic-cert-date" className="brutalist-input flex items-center justify-between cursor-pointer" type="button">
                  <span className={formData.cert_date ? "text-[#0A0A0A]" : "text-[#9CA3AF]"}>
                    {formData.cert_date ? format(formData.cert_date, "dd/MM/yyyy") : "Select date"}
                  </span>
                  <CalendarIcon className="w-4 h-4 text-[#71717A]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 border-2 border-[#0A0A0A] rounded-none" align="start">
                <Calendar mode="single" selected={formData.cert_date} onSelect={(d) => { updateForm({ cert_date: d }); setCalendarOpen(false); }} />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Contractor */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Particulars of the Contractor</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="form-field"><Label>Trading title</Label><Input testid="eic-contractor-title" value={formData.contractor_trading_title} onChange={set("contractor_trading_title")} /></div>
          <div className="form-field"><Label>Email</Label><Input testid="eic-contractor-email" value={formData.contractor_email} onChange={set("contractor_email")} /></div>
          <div className="form-field"><Label>Telephone</Label><Input testid="eic-contractor-phone" value={formData.contractor_phone} onChange={set("contractor_phone")} /></div>
          <div className="form-field"><Label>Website</Label><Input testid="eic-contractor-web" value={formData.contractor_web} onChange={set("contractor_web")} /></div>
          <div className="form-field"><Label>Registration No.</Label><Input testid="eic-contractor-reg" value={formData.contractor_reg_no} onChange={set("contractor_reg_no")} placeholder="N/A" /></div>
          <div className="form-field"><Label>Branch No.</Label><Input testid="eic-contractor-branch" value={formData.contractor_branch_no} onChange={set("contractor_branch_no")} placeholder="N/A" /></div>
          <div className="form-field md:col-span-2"><Label>Address</Label><Input testid="eic-contractor-address" value={formData.contractor_address} onChange={set("contractor_address")} /></div>
          <div className="form-field"><Label>Postcode</Label><Input testid="eic-contractor-postcode" value={formData.contractor_postcode} onChange={set("contractor_postcode")} /></div>
        </div>
      </div>

      {/* Next Inspection & Comments */}
      <div className="space-y-3 mb-6">
        <div className="form-field">
          <Label>Next inspection recommended interval</Label>
          <Input testid="eic-next-inspection" value={formData.next_inspection_interval} onChange={set("next_inspection_interval")} placeholder="e.g. 10 years" />
        </div>
        <div className="form-field">
          <Label>Comments on existing installation</Label>
          <TextArea testid="eic-comments" value={formData.comments} onChange={set("comments")} placeholder="N/A" rows={2} />
        </div>
      </div>

      <div className="flex justify-end">
        <button data-testid="eic-step-1-next" onClick={handleNext} className="brutalist-btn flex items-center gap-2">
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
